import React, { useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { 
  ArrowLeftIcon, 
  PrinterIcon, 
  ArrowDownTrayIcon,
  CalendarDaysIcon,
  UserCircleIcon,
  DocumentCheckIcon
} from '@heroicons/react/24/outline'
import { jsPDF } from 'jspdf'
import html2canvas from 'html2canvas'

// Interfaz para el Dictamen (Mock)
interface Dictamen {
  id: string
  titulo: string
  fecha: string
  autor: string
  cargo: string
  resumen: string
  contenido: string
  estado: 'aprobado' | 'pendiente' | 'rechazado'
  referencia: string
}

const PaginaDetalleDictamen: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const contentRef = useRef<HTMLDivElement>(null)
  const [generandoPDF, setGenerandoPDF] = useState(false)

  // Mock de datos (en una app real vendría de un servicio)
  const dictamen: Dictamen = {
    id: id || 'DICT-2024-001',
    titulo: 'Dictamen de Constitucionalidad sobre la Reforma Logística',
    fecha: '2024-01-15',
    autor: 'Dr. Alejandro Magno',
    cargo: 'Magistrado Ponente',
    resumen: 'Análisis detallado sobre la viabilidad constitucional de las nuevas regulaciones en el transporte de carga pesada y sus implicaciones en el comercio exterior.',
    contenido: `
      <h2>1. Antecedentes</h2>
      <p>El presente dictamen analiza la propuesta de reforma al reglamento de tránsito y transporte logístico presentada ante el congreso.</p>
      
      <h2>2. Fundamentos Jurídicos</h2>
      <p>Considerando los artículos 5, 11 y 24 de la Constitución, se establece el derecho al libre tránsito y la regulación del comercio.</p>
      
      <h2>3. Análisis de Constitucionalidad</h2>
      <p>La medida propuesta cumple con los criterios de proporcionalidad y necesidad. No se observan violaciones a los derechos fundamentales de los transportistas.</p>
      
      <h2>4. Conclusión</h2>
      <p>Se declara PROCEDENTE la reforma, sujeta a las observaciones mencionadas en el anexo técnico.</p>
      
      <p class="firma">Atentamente,<br/>Magistrado Alejandro Magno</p>
    `,
    estado: 'aprobado',
    referencia: 'EXP-2023-899'
  }

  const handleDownloadPDF = async () => {
    if (!contentRef.current) return
    
    try {
      setGenerandoPDF(true)
      
      const element = contentRef.current
      const canvas = await html2canvas(element, {
        scale: 2, // Mejorar calidad
        logging: false,
        useCORS: true
      } as any)
      
      const imgData = canvas.toDataURL('image/png')
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      })
      
      const imgWidth = 210
      const imgHeight = (canvas.height * imgWidth) / canvas.width
      
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight)
      pdf.save(`Dictamen-${dictamen.id}.pdf`)
      
    } catch (error) {
      console.error('Error al generar PDF:', error)
      alert('Hubo un error al generar el PDF. Por favor intente nuevamente.')
    } finally {
      setGenerandoPDF(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-12">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <button 
              onClick={() => navigate(-1)}
              className="mr-4 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
              {dictamen.id} - Detalle del Dictamen
            </h1>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => window.print()}
              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-gray-700 rounded-lg transition-colors hidden sm:block"
              title="Imprimir"
            >
              <PrinterIcon className="h-5 w-5" />
            </button>
            
            <button
              onClick={handleDownloadPDF}
              disabled={generandoPDF}
              className={`
                flex items-center px-4 py-2 rounded-lg text-sm font-medium text-white transition-all
                ${generandoPDF 
                  ? 'bg-blue-400 cursor-not-allowed' 
                  : 'bg-blue-600 hover:bg-blue-700 shadow-sm hover:shadow'
                }
              `}
            >
              <ArrowDownTrayIcon className={`h-4 w-4 mr-2 ${generandoPDF ? 'animate-bounce' : ''}`} />
              {generandoPDF ? 'Generando...' : 'Descargar PDF'}
            </button>
          </div>
        </div>
      </header>

      {/* Contenido Principal */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Vista previa del documento (Lo que se va a imprimir en PDF) */}
        <div 
          ref={contentRef} 
          className="bg-white text-gray-900 shadow-lg rounded-xl overflow-hidden print:shadow-none print:rounded-none"
          style={{ minHeight: '297mm' }} // Simular altura A4 mínima
        >
          {/* Encabezado del Documento */}
          <div className="bg-slate-900 text-white p-8 md:p-12 relative overflow-hidden">
            <div className="absolute top-0 right-0 p-4 opacity-10">
              <DocumentCheckIcon className="h-64 w-64 transform rotate-12" />
            </div>
            
            <div className="relative z-10">
              <div className="flex items-center space-x-2 mb-4 text-blue-300">
                <span className="px-2 py-1 bg-blue-500/20 rounded text-xs font-semibold uppercase tracking-wider border border-blue-500/30">
                  {dictamen.estado}
                </span>
                <span className="text-sm">•</span>
                <span className="text-sm font-mono">{dictamen.referencia}</span>
              </div>
              
              <h1 className="text-3xl md:text-4xl font-serif font-bold leading-tight mb-6">
                {dictamen.titulo}
              </h1>
              
              <div className="flex flex-wrap items-center gap-6 text-slate-300 text-sm">
                <div className="flex items-center">
                  <CalendarDaysIcon className="h-5 w-5 mr-2 opacity-70" />
                  {dictamen.fecha}
                </div>
                <div className="flex items-center">
                  <UserCircleIcon className="h-5 w-5 mr-2 opacity-70" />
                  <div>
                    <p className="font-medium text-white">{dictamen.autor}</p>
                    <p className="text-xs opacity-70">{dictamen.cargo}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Cuerpo del Documento */}
          <div className="p-8 md:p-12 space-y-8">
            <div className="prose prose-slate max-w-none">
              <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-blue-500 italic text-gray-700 mb-8">
                <h3 className="text-sm font-bold text-gray-900 uppercase mb-2 not-italic">Resumen Ejecutivo</h3>
                {dictamen.resumen}
              </div>
              
              <div 
                className="content-body font-serif text-lg leading-relaxed text-gray-800"
                dangerouslySetInnerHTML={{ __html: dictamen.contenido }}
              />
            </div>
            
            <div className="mt-16 pt-8 border-t border-gray-200 flex flex-col items-center justify-center text-center">
              <div className="w-64 h-20 mb-4 flex items-end justify-center border-b border-gray-400">
                <span className="font-serif italic text-2xl text-blue-900">Alejandro Magno</span>
              </div>
              <p className="font-bold text-gray-900">{dictamen.autor}</p>
              <p className="text-sm text-gray-500">{dictamen.cargo}</p>
              <p className="text-xs text-gray-400 mt-1">Documento firmado digitalmente</p>
            </div>
          </div>

          {/* Pie de página del documento */}
          <div className="bg-gray-50 px-8 py-4 border-t border-gray-100 flex justify-between items-center text-xs text-gray-500">
            <span>LogiXport - Sistema de Gestión Constitucional</span>
            <span>Página 1 de 1</span>
          </div>
        </div>
      </main>
    </div>
  )
}

export default PaginaDetalleDictamen
